import { z } from 'zod';

import type { CoinPackageList, CreateCoinOrder } from '@/shared/api/types';

/**
 * 고른 상품 — 주소창 검색 파라미터로 둔다 (충전 화면 → 로그인 → 결제 화면으로 넘어가도 유지된다).
 * package 와 coins 가 둘 다 있으면 package 를 쓴다.
 */
export const coinSelectionSearchSchema = z.object({
  /** 코인 패키지 ID */
  package: z.string().max(32).optional().catch(undefined),
  /** 맞춤 충전 수량 */
  coins: z.coerce.number().int().positive().optional().catch(undefined),
});

export type CoinSelectionSearch = z.infer<typeof coinSelectionSearchSchema>;

/** 카탈로그로 확인한 선택 — 금액 표시용이고, 실제 결제 금액은 주문을 만들 때 서버가 정한다 */
export type CoinSelection =
  | { kind: 'package'; packageId: string; coinAmount: number; price: number }
  | { kind: 'custom'; coinAmount: number; price: number };

/**
 * 검색 파라미터를 카탈로그로 확인한다.
 * @returns 판매 중인 패키지이거나 맞춤 수량 범위 안이면 선택, 아니면 null
 */
export function resolveSelection(
  search: CoinSelectionSearch,
  catalog: CoinPackageList,
): CoinSelection | null {
  if (search.package !== undefined) {
    const coinPackage = catalog.packages.find(item => item.id === search.package);
    return coinPackage
      ? {
          kind: 'package',
          packageId: coinPackage.id,
          coinAmount: coinPackage.coinAmount,
          price: coinPackage.price,
        }
      : null;
  }
  if (search.coins !== undefined) {
    const { minCoinAmount, maxCoinAmount, coinPrice } = catalog.custom;
    if (search.coins < minCoinAmount || search.coins > maxCoinAmount) return null;
    return { kind: 'custom', coinAmount: search.coins, price: search.coins * coinPrice };
  }
  return null;
}

/** 선택을 검색 파라미터로 바꾼다 (다른 쪽 키는 지운다) */
export function toSelectionSearch(selection: CoinSelection): CoinSelectionSearch {
  return selection.kind === 'package'
    ? { package: selection.packageId, coins: undefined }
    : { package: undefined, coins: selection.coinAmount };
}

/** 선택을 주문 생성 요청으로 바꾼다 */
export function toCreateOrderBody(selection: CoinSelection): CreateCoinOrder {
  return selection.kind === 'package'
    ? { packageId: selection.packageId }
    : { customCoinAmount: selection.coinAmount };
}
